/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import type { FilterDef, FilterOption, SearchAccessor, SortOption } from "./Directory.types";

/**
 * Build a unique-sorted FilterOption[] from a string-array attribute of a
 * data list. Useful for deriving chip / combobox options from fetched data.
 *
 * - Deduplicates via Set
 * - Case-insensitive locale-aware sort
 * - Skips falsy strings ("", null, undefined)
 */
export function deriveOptions<T>(items: T[], accessor: (item: T) => string[] | null | undefined): FilterOption[] {
	const set = new Set<string>();
	for (const item of items) {
		const list = accessor(item) ?? [];
		for (const value of list) {
			if (value) set.add(value);
		}
	}
	return Array.from(set)
		.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
		.map((value) => ({ value, label: value }));
}

/**
 * Test whether a value (from a SearchAccessor) matches a lowercase query string.
 * Arrays are flattened; nullish values never match.
 */
function searchAccessorMatches<T>(accessor: SearchAccessor<T>, item: T, query: string): boolean {
	if (accessor.type === "string") {
		const v = accessor.fn(item);
		return typeof v === "string" && v.toLowerCase().includes(query);
	}
	const vs = accessor.fn(item);
	if (!Array.isArray(vs)) return false;
	return vs.some((v) => typeof v === "string" && v.toLowerCase().includes(query));
}

/**
 * Evaluate a single filter against an item. Returns true if the item passes.
 * Engine dispatches purely by `kind`; accessor types are narrowed inside each branch.
 */
function itemPassesFilter<T>(
	filter: FilterDef<T>,
	item: T,
	searchQueries: Record<string, string>,
	singleValueFilters: Record<string, string>,
	multiValueFilters: Record<string, string[]>,
): boolean {
	switch (filter.kind) {
		case "search": {
			const query = (searchQueries[filter.paramKey] ?? "").trim().toLowerCase();
			if (!query) return true;
			return filter.accessors.some((a) => searchAccessorMatches(a, item, query));
		}
		case "tabs":
		case "select": {
			const selected = singleValueFilters[filter.paramKey] ?? "";
			if (!selected) return true;
			const v = filter.accessor(item);
			return v === selected;
		}
		case "tabsMulti": {
			const selected = singleValueFilters[filter.paramKey] ?? "";
			if (!selected) return true;
			const vs = filter.accessor(item);
			return Array.isArray(vs) && vs.includes(selected);
		}
		case "chips":
		case "combobox": {
			const selected = multiValueFilters[filter.paramKey] ?? [];
			if (selected.length === 0) return true;
			const vs = filter.accessor(item);
			if (!Array.isArray(vs)) return false;
			return filter.matchMode === "all" ? selected.every((s) => vs.includes(s)) : selected.some((s) => vs.includes(s));
		}
	}
}

export function applyFilters<T>(
	items: T[],
	filters: FilterDef<T>[],
	searchQueries: Record<string, string>,
	singleValueFilters: Record<string, string>,
	multiValueFilters: Record<string, string[]>,
): T[] {
	if (filters.length === 0) return items;
	return items.filter((item) =>
		filters.every((f) => itemPassesFilter(f, item, searchQueries, singleValueFilters, multiValueFilters)),
	);
}

export function applySort<T>(items: T[], option: SortOption<T>): T[] {
	const dirMult = option.direction === "desc" ? -1 : 1;
	const sorted = [...items];
	if (option.kind === "string") {
		// TS narrows accessor to StringAccessor<T>
		const accessor = option.accessor;
		sorted.sort((a, b) => {
			const av = accessor(a);
			const bv = accessor(b);
			// Null/undefined sorts last regardless of direction.
			if (av == null && bv == null) return 0;
			if (av == null) return 1;
			if (bv == null) return -1;
			return av.localeCompare(bv) * dirMult;
		});
	} else {
		const accessor = option.accessor;
		sorted.sort((a, b) => {
			const av = accessor(a);
			const bv = accessor(b);
			if (av == null && bv == null) return 0;
			if (av == null) return 1;
			if (bv == null) return -1;
			return (av - bv) * dirMult;
		});
	}
	return sorted;
}

export function sliceForPage<T>(items: T[], page: number, perPage: number): T[] {
	if (perPage <= 0) return items;
	const start = Math.max(0, (page - 1) * perPage);
	return items.slice(start, start + perPage);
}

// ————————————————————————————————————————————————————————————————————————————————
// URL param helpers
// ————————————————————————————————————————————————————————————————————————————————

/**
 * Serialize a multi-value filter to a comma-separated URL param.
 *
 * Values are deduplicated via Set, sorted for canonical URLs (so `?x=a,b` and
 * `?x=b,a` produce the same serialized form), and each value is
 * `encodeURIComponent`-wrapped so embedded commas survive a round-trip
 * through `parseMultiValueParam`.
 *
 * Single-value filters do NOT need this — `URLSearchParams.set(name, v)`
 * already encodes once on `.toString()`, and pre-encoding produces double
 * encoding. Only multi-value needs the explicit encode because we're
 * comma-joining values into a single param.
 */
export const encodeMultiValueParam = (values: string[]): string =>
	Array.from(new Set(values))
		.filter((v) => v !== "")
		.sort()
		.map(encodeURIComponent)
		.join(",");

/**
 * Parse a multi-value URL param. Critical: decode BEFORE splitting, so values that
 * contained encoded commas (`%2C`) survive intact.
 *
 * Accepts both comma-separated and repeated-param forms:
 *   ?x=a,b,c
 *   ?x=a&x=b&x=c
 *   ?x=a&x=b,c
 */
export const parseMultiValueParam = (params: URLSearchParams, key: string): string[] => {
	const raw = params.getAll(key);
	return raw.flatMap((v) => decodeURIComponent(v).split(",")).filter((v) => v !== "");
};

/** Compose `${prefix}_${paramKey}`. Underscore separator (matches ResellersSearch `rs*` convention). */
export const composeParamName = (prefix: string, paramKey: string): string => `${prefix}_${paramKey}`;

/**
 * Assertion that fires in dev — catches duplicate paramKeys within a single Directory
 * instance which would cause URL param collisions at runtime.
 */
export function assertUniqueParamKeys<T>(
	filters: FilterDef<T>[],
	sortParamKey?: string,
	paginationParamKey?: string,
): void {
	const keys = filters.map((f) => f.paramKey);
	if (sortParamKey) keys.push(sortParamKey);
	if (paginationParamKey) keys.push(paginationParamKey);
	const seen = new Set<string>();
	for (const k of keys) {
		if (seen.has(k)) {
			throw new Error(
				`[Directory] Duplicate paramKey "${k}" — every filter/sort/pagination paramKey must be unique within a Directory instance.`,
			);
		}
		seen.add(k);
	}
}
